export type Diagnosis = { code: string; description: string; custom: boolean };
export type TestSet = { id?: string; previous_set_id?: string; tests: string[]; when: string; interval: string; unit: string; timing: string; target_date?: string; instructions: string };
export type InternalDetailsData = {
  diagnoses: Diagnosis[];
  primary_diagnosis: string;
  billing: { choice: string; code: string; description: string; system: string; modifier: string };
  plan: string;
  categories: string[];
  no_action: boolean;
  fields: Record<string, Record<string, string>>;
  test_sets: TestSet[];
};
export type DetailsResponse = {
  labSlipsUrl?: string;
  noteVisitDate?: string | null;
  linkedConsult?: { id: number; date: string; scheduled: boolean } | null;
  linkConsultUrl?: string;
  availableConsults?: { id: number; date: string; time: string | null; noteId: number | null; planVersion: number; hasSavedPlan: boolean }[];
  completionAvailable?: boolean;
  completedAt?: string | null;
  billingEmailStatus?: string | null;
  billingEmailSentAt?: string | null;
  dailyEmailStatus?: string | null;
  dailyEmailSentAt?: string | null;
  changedSinceEmail?: boolean;
  extractUrl?: string;
  data: Partial<InternalDetailsData>;
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  canEdit: boolean;
  catalog: { diagnoses: Record<string, string>; billing: Record<string, string>; categories: Record<string, string>; tests: string[] };
};
export function normalizeDetails(data: Partial<InternalDetailsData>): InternalDetailsData {
  const categories = data.categories || [];
  const hasLabs = !data.no_action && categories.includes("labs");
  const hasResults = !data.no_action && (hasLabs || categories.includes("results"));
  const results = data.fields?.results || {};
  return {
    diagnoses: data.diagnoses || [], primary_diagnosis: data.primary_diagnosis || "",
    billing: { choice: "", code: "", description: "", system: "", modifier: "", ...data.billing },
    plan: data.plan || "", categories, no_action: data.no_action || false,
    fields: hasResults ? { ...data.fields, results: {
      ...results,
      review_when: results.review_when || (results.method || results.instructions ? "" : "No patient discussion"),
      method: ["Pre-op review only", "No patient discussion"].includes(results.review_when) ? "" : results.method || "",
    } } : data.fields || {}, test_sets: data.test_sets || [],
  };
}
export function emptyTestSet(): TestSet {
  return { id: crypto.randomUUID(), tests: [], when: "", interval: "", unit: "weeks", timing: "", instructions: "" };
}
export function defaultImagingDiagnosis(data: InternalDetailsData, previousPrimary?: string): InternalDetailsData {
  if (data.no_action || !data.categories.includes("imaging")) return data;
  const imaging = data.fields.imaging || {};
  // Keep a separate imaging indication, but let the default follow the primary code.
  if (imaging.diagnosis?.trim() && imaging.diagnosis !== previousPrimary) return data;
  return { ...data, fields: { ...data.fields, imaging: { ...imaging, diagnosis: data.primary_diagnosis } } };
}
export type PlanField = { key: string; label: string; options?: string[]; multiline?: boolean; placeholder?: string };
export const PLAN_FIELDS: Record<string, PlanField[]> = {
  surgery: [
    { key: "facility", label: "Facility", options: ["ASC", "Desert / BDMC", "Either", "Other"] },
    { key: "scan", label: "Scan", options: ["With scan", "No scan", "Conditional", "Undecided"] },
    { key: "procedure", label: "Procedure", options: ["Standard", "Reoperative", "Other"] },
    { key: "readiness", label: "Readiness", options: ["Ready to offer dates", "Patient deciding", "Awaiting workup"] },
    { key: "timing", label: "Timing preference", placeholder: "Earliest date, preferred dates, exclusions" },
    { key: "instructions", label: "Surgery instructions", multiline: true, placeholder: "Facility details, scan conditions, case order, overnight stay…" },
  ],
  labs: [
    { key: "instructions", label: "Additional lab instructions", multiline: true, placeholder: "Exact wording, already issued orders, or special requirements" },
  ],
  urine: [
    { key: "urine", label: "24-hour urine", options: ["Not requested", "Calcium", "Calcium and creatinine", "Other"] },
    { key: "timing", label: "Urine timing", placeholder: "When the urine test should be done" },
    { key: "instructions", label: "Urine test instructions", multiline: true, placeholder: "Collection details, already issued orders, or special requirements" },
  ],
  imaging: [
    { key: "studies", label: "Imaging studies", multiline: true, placeholder: "Sestamibi / SPECT, SPECT/CT, 4D CT, thyroid ultrasound, renal ultrasound, or other" },
    { key: "location", label: "Imaging location", placeholder: "Local or named facility" },
    { key: "timing", label: "Imaging timing" },
    { key: "diagnosis", label: "Imaging diagnosis / code" },
    { key: "instructions", label: "Imaging instructions", multiline: true, placeholder: "Required, conditional, or already ordered; exact clinical instructions" },
  ],
  records: [
    { key: "records", label: "Records / clearance needed", multiline: true, placeholder: "Cardiology note, clearance, thyroid ultrasound, FNA, echo, DEXA, or other" },
    { key: "provider", label: "Provider / facility" },
    { key: "timing", label: "Record dates / timing" },
    { key: "prerequisite", label: "Prerequisite", options: ["For information", "Preferred before surgery", "Required before booking", "Required before surgery", "Required before review"] },
    { key: "instructions", label: "Records instructions", multiline: true },
  ],
  follow_up: [
    { key: "visit_type", label: "Visit type", options: ["Telehealth", "In-person", "Other"] },
    { key: "interval", label: "Follow-up interval", placeholder: "e.g. 6" },
    { key: "unit", label: "Follow-up interval unit", options: ["Days after consult", "Weeks after consult", "Months after consult"] },
    { key: "timing", label: "Follow-up timing notes (optional)", placeholder: "Specific date, conditions, or exceptions to the timing above" },
    { key: "instructions", label: "Follow-up instructions", multiline: true, placeholder: "Tests needed before the visit" },
  ],
  results: [
    { key: "review_when", label: "Results review", options: ["No patient discussion", "After all requested results are available", "As each result becomes available", "Pre-op review only", "Other"] },
    { key: "method", label: "Preferred method", options: ["Email", "Phone", "Visit", "Other"] },
    { key: "instructions", label: "Results discussion instructions", multiline: true },
  ],
  genetics: [
    { key: "test", label: "Genetic test / referral", placeholder: "Invitae, specialist referral, or other" },
    { key: "status", label: "Order status", options: ["Needs ordering", "Already ordered", "Awaiting outside results"] },
    { key: "timing", label: "Genetics / referral timing" },
    { key: "instructions", label: "Genetics / referral instructions", multiline: true },
  ],
  other: [
    { key: "description", label: "Other plan instructions", multiline: true },
    { key: "timing", label: "Other plan timing" },
  ],
};
